import { createComponent, createHook } from 'reakit-system';
import { useContext, useMemo } from 'react';
import { pxToRem } from '@mos-cat/ds-theme';
import type { FieldOptions, FieldProps } from '../Field/Field';
import { useField } from '../Field/Field';
import { searchContext } from './internal/context';
import { createLanguageGetter } from './internal/text';

const iconBg = `data:image/svg+xml;utf8,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M18.0068 11.5032C18.0068 7.91158 15.0952 5 11.5034 5C7.91168 5 5 7.91158 5 11.5032C5 15.0948 7.91168 18.0064 11.5034 18.0064C12.7713 18.0064 13.9545 17.6436 14.9547 17.0161L17.7193 19.7805C18.0122 20.0734 18.4873 20.0731 18.7802 19.7802C19.0731 19.4873 19.0734 19.0122 18.7805 18.7193L16.1323 16.0712C17.2914 14.8968 18.0068 13.2836 18.0068 11.5032ZM11.5034 6.50074C8.74054 6.50074 6.50079 8.74041 6.50079 11.5032C6.50079 14.266 8.74054 16.5056 11.5034 16.5056C14.2663 16.5056 16.506 14.266 16.506 11.5032C16.506 8.74041 14.2663 6.50074 11.5034 6.50074Z" fill="%235D6570"/></svg>`;

export const useSearchField = createHook<
  FieldOptions,
  Omit<
    FieldProps,
    'type' | 'aria-activedescendant' | 'aria-controls' | 'aria-autocomplete'
  >
>({
  compose: [useField],
  useOptions(options) {
    return {
      ...options,
      styles: {
        ...options.styles,
        maxWidth: 1,
        pl: ['fieldPaddingX', 'sp-48'],
        backgroundColor: 'white',
        backgroundImage: ['none', `url('${iconBg}')`],
        backgroundPosition: pxToRem(11),
        backgroundRepeat: 'no-repeat',
      },
    };
  },
  useProps(_, props) {
    const { lang } = useContext(searchContext);
    const getText = useMemo(() => createLanguageGetter(lang), [lang]);
    return {
      autoComplete: 'off',
      'aria-autocomplete': 'list',
      ...props,
      type: 'search',
      placeholder: getText('placeholder'),
    };
  },
});

export const SearchField = createComponent({
  as: 'input',
  useHook: useSearchField,
});
